import { type ReactElement, useState } from "react";
import { graphql, useMutation } from "react-relay";
import { Button, Dialog, toast } from "@olivahealth/oli-ui";
import UserSelector from "./UserSelector";
import type { DisconnectSlackUserMutation as DisconnectSlackUserMutationType } from "./__generated__/DisconnectSlackUserMutation.graphql";

const DisconnectSlackUserMutation = graphql`
  mutation DisconnectSlackUserMutation($userId: ID!) {
    disconnectSlack(userId: $userId) {
      __typename
      ... on SlackDisconnected {
        success
        userId
        message
      }
      ... on ForbiddenError {
        reason
      }
      ... on ValidationError {
        reason
      }
    }
  }
`;

export default function DisconnectSlackUser(): ReactElement {
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [mutate, isDisconnecting] =
    useMutation<DisconnectSlackUserMutationType>(DisconnectSlackUserMutation);

  const handleDisconnectSlack = () => {
    if (!selectedUserId) return;

    mutate({
      variables: { userId: selectedUserId },
      onCompleted: (response) => {
        setIsDialogOpen(false);
        if (response.disconnectSlack.__typename === "SlackDisconnected") {
          toast({
            body: response.disconnectSlack.message,
            variant: "success",
          });
          setSelectedUserId("");
        } else if (response.disconnectSlack.__typename === "ForbiddenError") {
          toast({ body: response.disconnectSlack.reason, variant: "error" });
        } else if (response.disconnectSlack.__typename === "ValidationError") {
          toast({ body: response.disconnectSlack.reason, variant: "error" });
        }
      },
      onError: (error) => {
        setIsDialogOpen(false);
        toast({ body: error.message, variant: "error" });
      },
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-lg font-semibold">Disconnect User from Slack</h3>
      <div className="flex gap-4">
        <div className="flex-1">
          <UserSelector
            onUserSelected={(user) => setSelectedUserId(user.id)}
            placeholder="Search for a user..."
            includeOlivans
          />
        </div>
        <Button
          disabled={!selectedUserId || isDisconnecting}
          onClick={() => setIsDialogOpen(true)}
          variant="danger"
        >
          Disconnect from Slack
        </Button>
      </div>
      <Dialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        variant="centered"
        size="sm"
        showCloseButton={false}
      >
        <div className="flex flex-col items-center gap-y-4 text-center">
          <h3 className="text-lg font-semibold">Disconnect User from Slack</h3>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            This will disconnect this user from Slack. Their Slack token and
            user ID will be removed.
          </p>
          <div className="flex gap-4">
            <Button
              variant="danger"
              onClick={handleDisconnectSlack}
              disabled={isDisconnecting}
            >
              {isDisconnecting ? "Disconnecting..." : "Disconnect"}
            </Button>
            <Button
              variant="secondary"
              onClick={() => setIsDialogOpen(false)}
              disabled={isDisconnecting}
              className="dark:text-white dark:border-white"
            >
              Cancel
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
